import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// While a grid home's widgets are being edited (home_desktop_controller's
// .is-editing), the quick-action chips under Jane's ask box drag left and
// right to reorder; at rest they are buttons and links as usual. A pointer
// drag rather than HTML5 drag, and the pointerdown stops here: the chips sit
// inside the ask card, which edit mode would otherwise pick up and move. The
// order saves on release (PATCH /home/actions/order).
export default class extends Controller {
    static targets = ["slot"]
    static values = { url: String }

    connect() {
        this._onDown = (event) => this._down(event)
        // Capture: the chips are links and buttons, and in edit mode a click
        // must neither follow one nor reach the grid.
        this._onClick = (event) => {
            if (this._editing() || this._justDragged) {
                event.preventDefault()
                event.stopPropagation()
            }
        }
        this.element.addEventListener("pointerdown", this._onDown)
        this.element.addEventListener("click", this._onClick, true)
    }

    disconnect() {
        this.element.removeEventListener("pointerdown", this._onDown)
        this.element.removeEventListener("click", this._onClick, true)
        this._stop()
    }

    _editing() {
        return Boolean(this.element.closest(".is-editing"))
    }

    _down(event) {
        if (!this._editing() || event.button !== 0 || !this.urlValue) return
        const slot = event.target.closest("[data-quick-action-order-target~='slot']")
        if (!slot) return
        event.stopPropagation()
        event.preventDefault()
        this._drag = { slot, startX: event.clientX, moved: false, before: this._ids().join(",") }
        this._onMove = (e) => this._move(e)
        this._onUp = () => this._up()
        document.addEventListener("pointermove", this._onMove)
        document.addEventListener("pointerup", this._onUp)
        document.addEventListener("pointercancel", this._onUp)
    }

    _move(event) {
        const drag = this._drag
        if (!drag) return
        if (!drag.moved) {
            if (Math.abs(event.clientX - drag.startX) < 4) return
            drag.moved = true
            drag.slot.classList.add("is-dragging")
            this.element.classList.add("is-reordering")
        }
        // The slot the pointer is over, by its horizontal middle: before it on
        // its left half, after it on its right.
        const over = this.slotTargets.find((slot) => {
            if (slot === drag.slot) return false
            const box = slot.getBoundingClientRect()
            return event.clientX >= box.left && event.clientX <= box.right
        })
        if (!over) return
        const box = over.getBoundingClientRect()
        const before = event.clientX < box.left + box.width / 2
        this.element.insertBefore(drag.slot, before ? over : over.nextSibling)
    }

    _up() {
        const drag = this._drag
        this._stop()
        if (!drag?.moved) return
        drag.slot.classList.remove("is-dragging")
        this.element.classList.remove("is-reordering")
        this._justDragged = true
        setTimeout(() => { this._justDragged = false }, 0)
        const ids = this._ids()
        if (ids.join(",") === drag.before) return
        fetch(this.urlValue, {
            method: "PATCH",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ ids })
        }).catch(() => {})
    }

    _stop() {
        this._drag = null
        document.removeEventListener("pointermove", this._onMove)
        document.removeEventListener("pointerup", this._onUp)
        document.removeEventListener("pointercancel", this._onUp)
    }

    _ids() {
        return this.slotTargets.map((slot) => slot.dataset.actionId)
    }
}
